import { Button, Modal } from '@dev-spendesk/grapes';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

type Props = {
  isOpen: boolean;
  reason: string | undefined;
  onClose: () => void;
};

export function AccountingBaseSwitchFailureModal({
  isOpen,
  onClose,
  reason,
}: Props) {
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();

  const reasonMessage = getSwitchErrorMessage({ reason });

  const getModalActions = (): React.JSX.Element | undefined => {
    switch (reason) {
      case 'cannotCreateExpenseAccountDuplicates':
        return (
          <>
            <Button
              variant="secondaryNeutral"
              text={t('misc.close')}
              onClick={onClose}
            />
            <Button
              variant="primaryBrand"
              text={t(
                'bookkeep.integrations.settings.baseSwitch.error.userRedirectCTA',
              )}
              onClick={() => {
                history.push(
                  routeFor(routes.COMPANY_ACCOUNTING_EXPENSE_ACCOUNTS.path, {
                    company: companyId,
                  }),
                );
                onClose();
              }}
            />
          </>
        );
      default:
        return (
          <Button
            variant="secondaryNeutral"
            text={t('misc.close')}
            onClick={onClose}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bookkeep.integrations.settings.baseSwitch.error.title')}
      iconName="hexagone-cross"
      iconVariant="alert"
      actions={getModalActions()}
    >
      {reasonMessage ? (
        <>
          <p>{t(reasonMessage.cause)}</p>
          <Trans i18nKey={reasonMessage.fix as string}>
            text
            <strong className="text-content-primary title-m">path</strong>
            text
          </Trans>
        </>
      ) : (
        <p>{t('bookkeep.integrations.settings.baseSwitch.error.generic')}</p>
      )}
    </Modal>
  );
}

const getSwitchErrorMessage = ({
  reason,
}: {
  reason: string | undefined;
}): {
  cause: I18nKey;
  fix: I18nKey;
} | null => {
  switch (reason) {
    case 'cannotCreateExpenseAccountDuplicates':
      return {
        cause:
          'bookkeep.integrations.settings.baseSwitch.error.cannotCreateExpenseAccountDuplicates.cause',
        fix: 'bookkeep.integrations.settings.baseSwitch.error.cannotCreateExpenseAccountDuplicates.fix',
      };
    default:
      return null;
  }
};
